/**
 * Shape → SVG helpers shared by the shape gallery (mini previews) and the
 * on-page overlay (live placement preview). Both render a shape's geometry in a
 * normalized 0..1 box; the gallery draws it at a fixed viewBox, the overlay
 * stretches it to the placed bounds via preserveAspectRatio="none".
 *
 * The single source of truth for geometry is @pdf-ticket/editor's
 * createPowerPointShapePath — the same generator whose PDF-space output the
 * driver fills/strokes on save, so preview and saved bytes agree.
 */
import {
  createPowerPointShapePath,
  canCreatePowerPointShapePath,
  markStampShapeTypes,
  shapeAdjustSpec,
  shapeAdjustDefaults,
} from '@pdf-ticket/editor';
import type { ShapePathSegment } from '@pdf-ticket/pdf-core';

// Re-export the adjustment spec helpers so the overlay has one import source.
export { shapeAdjustSpec, shapeAdjustDefaults };

export interface Rgb {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

/** PowerPoint-ish default autoshape style (accent blue fill, darker outline). */
export const DEFAULT_SHAPE_FILL: Rgb = { r: 0.27, g: 0.45, b: 0.77 };
export const DEFAULT_SHAPE_STROKE: Rgb = { r: 0.16, g: 0.28, b: 0.5 };
export const DEFAULT_SHAPE_STROKE_WIDTH = 1;

export const isShapePathReady = canCreatePowerPointShapePath;

// --- Mark / stamp glyphs (✓ ✗ ○ ● ◎) -------------------------------------
// Marks reuse the entire add-shape pipeline (placement / move / resize / style /
// multi-select). They differ from ordinary autoshapes only in their default
// paint: outline marks (✓ ✗ ○ ◎) are stroked with no fill; ● is a solid dot.

const MARK_SET: ReadonlySet<string> = new Set(markStampShapeTypes);
/** True for the ✓ ✗ ○ ● ◎ glyph shape types. */
export const isMarkShape = (shapeType: string): boolean => MARK_SET.has(shapeType);
/** True for marks rendered as a solid fill (●) rather than an outline. */
export const isFilledMark = (shapeType: string): boolean => shapeType === 'MarkDot';

export interface MarkStyle {
  readonly fill: Rgb | null;
  readonly stroke: Rgb | null;
  readonly strokeWidth: number;
  readonly opacity: number;
}

const MARK_GREEN: Rgb = { r: 0.13, g: 0.55, b: 0.2 };
const MARK_RED: Rgb = { r: 0.8, g: 0.13, b: 0.13 };
const MARK_INK: Rgb = { r: 0.1, g: 0.1, b: 0.1 };
const MARK_STROKE_WIDTH = 3;

/** Default paint for a freshly-armed mark (overridable in the style bar). */
export function markDefaultStyle(shapeType: string): MarkStyle {
  if (shapeType === 'MarkDot') {
    return { fill: MARK_INK, stroke: null, strokeWidth: 1, opacity: 1 };
  }
  const stroke = shapeType === 'MarkCheck' ? MARK_GREEN : MARK_RED;
  return { fill: null, stroke, strokeWidth: MARK_STROKE_WIDTH, opacity: 1 };
}

/**
 * Resolve a shape's geometry in PDF user space for the given bounds (origin
 * bottom-left, Y up) — exactly what the `add-shape` Edit carries.
 */
export function shapePathInPdf(
  shapeType: string,
  bounds: { x: number; y: number; width: number; height: number },
): ReadonlyArray<ShapePathSegment> | null {
  const path = createPowerPointShapePath(shapeType, bounds);
  return path ? (path as ReadonlyArray<ShapePathSegment>) : null;
}

/**
 * Build an SVG path `d` for a shape, mapping its normalized [0..1] Y-up geometry
 * into SVG's Y-down viewBox of `size` × `size`. Returns null for unknown types.
 */
export function shapeSvgPathD(
  shapeType: string,
  size = 100,
  adjustments?: readonly number[],
): string | null {
  const path = createPowerPointShapePath(
    shapeType,
    { x: 0, y: 0, width: 1, height: 1 },
    adjustments ? [...adjustments] : undefined,
  ) as ReadonlyArray<ShapePathSegment> | undefined;
  if (!path) return null;
  const fx = (x: number) => (x * size).toFixed(2);
  const fy = (y: number) => ((1 - y) * size).toFixed(2); // Y-up (PDF) → Y-down (SVG)
  return path
    .map((c) => {
      switch (c.type) {
        case 'move':
          return `M${fx(c.x)} ${fy(c.y)}`;
        case 'line':
          return `L${fx(c.x)} ${fy(c.y)}`;
        case 'cubic':
          return `C${fx(c.x1)} ${fy(c.y1)} ${fx(c.x2)} ${fy(c.y2)} ${fx(c.x)} ${fy(c.y)}`;
        case 'close':
          return 'Z';
      }
    })
    .join(' ');
}

/** CSS rgb() string from a 0–1 Rgb. */
export function rgbCss(c: Rgb, alpha = 1): string {
  const ch = (n: number) => Math.round(Math.max(0, Math.min(1, n)) * 255);
  return alpha >= 1 ? `rgb(${ch(c.r)}, ${ch(c.g)}, ${ch(c.b)})` : `rgba(${ch(c.r)}, ${ch(c.g)}, ${ch(c.b)}, ${alpha})`;
}
